import { useEffect, useState } from "react";
import DynamicEditForm from "../DynamicEditForm";
import ModalLoading from "../Modal/ModalLoading";
import useQueryEffect from "/src/hooks/useQueryEffect";
import { useAddPlotMutation, useUpdatePlotMutation } from "/src/redux/plotsApi";
import { useGetPlotQuery } from '/src/redux/plotsApi';
import Alert from '../Alert';
import LoadingSpinner from '../LoadingSpinner';

const formConfig = {
    autoFocus: false,
    fields: [
        {
            key: 'section',
            title: 'Section',
            type: 'text',
            placeholder: 'X',
            required: true,
            max: 1,
        },
        {
            key: 'lot',
            title: 'Lot Number',
            type: 'number',
            placeholder: '000',
            required: true,
            min: 0,
        },
        {
            key: 'lotPart',
            title: 'Lot Letter',
            type: 'text',
            placeholder: 'X',
            required: false,
            max: 2,
        },
        {
            key: 'notes',
            title: 'Notes',
            type: 'textarea',
            placeholder: '...',
            required: false,
            max: 1024,
        },
        {
            key: 'sizeNotes',
            title: 'Size Notes',
            type: 'textarea',
            placeholder: '...',
            required: false,
            max: 1024,
        },
        {
            key: 'sqf',
            title: 'SQF',
            type: 'number',
            placeholder: '0',
            required: false,
            min: 0,
        },
        {
            key: 'price',
            title: 'Price',
            type: 'number',
            placeholder: '0',
            required: false,
            min: 0,
            step: 0.01,
        },
    ]
}

export default function MapEditorPlotEditor({_id, lat, lng, onSave, onError, onDiscard}){
    const { data: queryData, error: queryError, isLoading } = useGetPlotQuery(_id, {skip: !_id});
    const [updatePlot, { error: mutationError, isLoading: isUpdating, isSuccess: isUpdateSuccess }] = useUpdatePlotMutation();
    const [createPlot, { error: createError, isLoading: isCreating, isSuccess: isCreateSuccess, data: createResult, }] = useAddPlotMutation();
    const [details, setDetails] = useState(null);

    // callback runs on success/error
    // notify parent of state
    const userTookAction = useQueryEffect((error)=>{
        if(error){
            onError(error);        
        }else{
            console.debug(`successfully ${isUpdateSuccess?'updated':'saved'} ${_id || 'new'}`);
            onSave(isCreateSuccess? createResult._id : null);
        }
    }, [isUpdateSuccess, isCreateSuccess],[mutationError, createError]);

    // copy data to state so details can be edited
    useEffect(()=>{
        setDetails((_id)? queryData : {});
    }, [_id, queryData]);

    // handle user clicking save plot details button
    function saveDetails(data){

        for(let key of ['notes','sizeNotes']){
            if(data[key]){
                const test = data[key].replace(/\s/g, '').length;
                if(!test) data[key] = null;
            }
        }

        userTookAction();
        if(_id){
            updatePlot({
                ...data,
                _id, 
            });
        }else{
            createPlot({
                ...data,
                lat,
                lng,
            });
        }
    };

    // handle user clicking discard button or pressing esc
    function discardDetails(){
        onDiscard();
    }

    return (
        <div>
            <h4 className='text-xl font-light mb-4'>Plot Information</h4>
            {(isUpdating || isCreating) && <ModalLoading title="Saving" absolute="true" />}
            {(queryError && (
                <Alert className='border-t-red-600'><p>Failed to load plot</p></Alert>
            )) || (isLoading && (
                <Alert><LoadingSpinner className='w-6' /></Alert>
            )) || (
                <DynamicEditForm 
                    formConfig={formConfig} 
                    selectedAsset={details} 
                    onSave={data=>saveDetails(data)} 
                    onDiscard={e=>discardDetails()}
                />
            )}
        </div>
    )
}